import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import {
  ConnectionState,
  DisconnectReason,
  Room,
  RoomEvent,
  Track,
  type RemoteParticipant,
  type RemoteTrack,
  type Participant,
} from "livekit-client";

import { useStore } from "../store";
import { useVoiceStore, type VoiceTokenInfo } from "./store";

/**
 * Holds the LiveKit Room instance plus per-identity audio elements. The
 * Room is created once we receive a voice:token message from the Colyseus
 * server and torn down when the Colyseus room changes or unmounts.
 */
interface LiveKitContextValue {
  room: Room | null;
  /** Lookup the underlying audio element so mute-others can adjust volume. */
  getAudioElement: (identity: string) => HTMLAudioElement | undefined;
  /** Lookup the active remote track (used by VU meter hook). */
  getRemoteTrack: (identity: string) => RemoteTrack | undefined;
}

const Ctx = createContext<LiveKitContextValue>({
  room: null,
  getAudioElement: () => undefined,
  getRemoteTrack: () => undefined,
});

export function useLiveKit(): LiveKitContextValue {
  return useContext(Ctx);
}

export function LiveKitProvider(props: { children: React.ReactNode }): JSX.Element {
  const colyseusRoom = useStore((s) => s.currentRoom);
  const remotePlayers = useStore((s) => s.remotePlayers);
  const mutedKeys = useVoiceStore((s) => s.mutedKeys);

  // Persistent refs: the LiveKit Room and the per-identity audio elements +
  // tracks. Kept on refs so identity lookups don't trigger re-renders.
  const roomRef = useRef<Room | null>(null);
  const audioElsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const tracksRef = useRef<Map<string, RemoteTrack>>(new Map());

  // Subscribe to Colyseus voice:token messages, build a Room, connect.
  useEffect(() => {
    if (!colyseusRoom) return;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectCount = 0;
    const off = colyseusRoom.onMessage("voice:token", (payload: VoiceTokenInfo) => {
      reconnectCount = 0;
      void connect(payload);
    });
    // Server sends voice:token in onJoin, but if this effect mounts after
    // that send (HMR remount, slow client subscribe) we'd miss it. Ask
    // explicitly — the server's onMessage("voice:request") echoes back.
    try {
      colyseusRoom.send("voice:request");
    } catch {
      // Room may not be ready yet; the onJoin send still works.
    }
    return () => {
      off();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      void disconnect();
    };

    async function connect(payload: VoiceTokenInfo): Promise<void> {
      // Tear down any previous Room first (e.g. lobby → arena transition).
      await disconnect();
      useVoiceStore.getState().setTokenInfo(payload);
      useVoiceStore.getState().setConn("connecting");

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        // We never publish video; only mic.
        publishDefaults: { dtx: true, red: true },
      });
      roomRef.current = room;
      wireRoomEvents(room);

      try {
        await room.connect(payload.url, payload.token, { autoSubscribe: true });
        useVoiceStore.getState().setConn("connected");
        // Firefox + Chrome block <audio> autoplay until a user gesture. The
        // analyser-based VU meter still works (drives the face glow), but
        // playback is silent. Arm a one-shot gesture listener that calls
        // room.startAudio() so the first click/tap unlocks audio. If the
        // user already gestured (e.g. they clicked the mic button), this
        // resolves immediately.
        if (!room.canPlaybackAudio) {
          installAudioUnlock(room);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[voice] connect failed", err);
        useVoiceStore
          .getState()
          .setConn("error", err instanceof Error ? err.message : String(err));
      }
    }

    async function disconnect(): Promise<void> {
      const room = roomRef.current;
      if (!room) return;
      roomRef.current = null;
      audioElsRef.current.clear();
      tracksRef.current.clear();
      try {
        await room.disconnect();
      } catch {
        /* ignore */
      }
      useVoiceStore.getState().setConn("idle");
      useVoiceStore.getState().setTokenInfo(null);
      useVoiceStore.getState().setMicEnabled(false);
      useVoiceStore.getState().setActiveSpeakers(new Set());
      useVoiceStore.getState().setRemoteIdentities(new Set());
    }

    function wireRoomEvents(room: Room): void {
      room.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
        if (track.kind !== Track.Kind.Audio) return;
        const identity = participant.identity;
        tracksRef.current.set(identity, track as RemoteTrack);
        // Attach to a fresh <audio> element so the browser plays it. We hold
        // a ref so we can mute it later (per-player local mute).
        const el = track.attach() as HTMLAudioElement;
        el.dataset.identity = identity;
        // Off-screen, but in the DOM so audio actually plays in browsers
        // that gate <audio> playback to attached elements.
        document.body.appendChild(el);
        audioElsRef.current.set(identity, el);
        applyLocalMutes();
      });

      room.on(RoomEvent.TrackUnsubscribed, (track, _pub, participant) => {
        const identity = participant.identity;
        track.detach().forEach((el) => el.remove());
        audioElsRef.current.delete(identity);
        tracksRef.current.delete(identity);
      });

      const updateRemotes = (): void => {
        const ids = new Set<string>();
        room.remoteParticipants.forEach((p: RemoteParticipant) => ids.add(p.identity));
        useVoiceStore.getState().setRemoteIdentities(ids);
      };
      room.on(RoomEvent.ParticipantConnected, updateRemotes);
      room.on(RoomEvent.ParticipantDisconnected, updateRemotes);

      room.on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
        const ids = new Set<string>();
        speakers.forEach((p) => ids.add(p.identity));
        useVoiceStore.getState().setActiveSpeakers(ids);
      });

      room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
        if (!room.canPlaybackAudio) installAudioUnlock(room);
      });

      room.on(RoomEvent.Disconnected, (reason) => {
        useVoiceStore.getState().setConn("idle");
        // Skip auto-reconnect for terminal reasons. CLIENT_INITIATED = our
        // own disconnect() helper. ROOM_DELETED / PARTICIPANT_REMOVED /
        // DUPLICATE_IDENTITY / SERVER_SHUTDOWN won't recover from a retry.
        if (
          reason === DisconnectReason.CLIENT_INITIATED ||
          reason === DisconnectReason.ROOM_DELETED ||
          reason === DisconnectReason.PARTICIPANT_REMOVED ||
          reason === DisconnectReason.DUPLICATE_IDENTITY ||
          reason === DisconnectReason.SERVER_SHUTDOWN
        ) {
          return;
        }
        // Don't fight the SDK's own reconnect path — Room emits Reconnecting
        // / Reconnected for transient blips, Disconnected only after giving
        // up. Only retry if no other attempt is in flight on this room.
        if (roomRef.current !== room) return;
        if (room.state !== ConnectionState.Disconnected) return;
        const payload = useVoiceStore.getState().tokenInfo;
        if (!payload) return;
        reconnectAttempt(payload);
      });
    }

    function installAudioUnlock(room: Room): void {
      const unlock = (): void => {
        if (roomRef.current !== room) return;
        if (room.canPlaybackAudio) return;
        void room.startAudio().catch((err) => {
          // eslint-disable-next-line no-console
          console.warn("[voice] startAudio failed", err);
        });
      };
      const opts = { once: true, capture: true } as const;
      window.addEventListener("pointerdown", unlock, opts);
      window.addEventListener("keydown", unlock, opts);
      window.addEventListener("touchstart", unlock, opts);
    }

    function reconnectAttempt(payload: VoiceTokenInfo): void {
      if (reconnectTimer != null) return;
      if (reconnectCount >= 4) {
        useVoiceStore.getState().setConn("error", "voice disconnected — refresh to retry");
        return;
      }
      // Exponential backoff: 1.5s, 3s, 6s, 12s.
      const delay = 1500 * Math.pow(2, reconnectCount);
      reconnectCount += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void connect(payload).then(() => {
          if (useVoiceStore.getState().conn === "connected") reconnectCount = 0;
        });
      }, delay);
    }
  }, [colyseusRoom]);

  // Re-apply local mutes whenever the mute set or the remote player list
  // changes. This handles both "I just muted Alice" and "Alice just joined
  // and I had muted her before" cases.
  useEffect(() => {
    applyLocalMutes();
    function applyLocalMutes(): void {
      const muted = useVoiceStore.getState().mutedKeys;
      audioElsRef.current.forEach((el, identity) => {
        const player = remotePlayers.get(identity);
        const key =
          player && player.pubkey && player.pubkey.length > 0
            ? `pk:${player.pubkey}`
            : `sid:${identity}`;
        el.muted = muted.has(key);
      });
    }
  }, [mutedKeys, remotePlayers]);

  // Local helper, also called inline from TrackSubscribed above (closure
  // capture is fine since it reads from refs/store).
  function applyLocalMutes(): void {
    const muted = useVoiceStore.getState().mutedKeys;
    const remotes = useStore.getState().remotePlayers;
    audioElsRef.current.forEach((el, identity) => {
      const player = remotes.get(identity);
      const key =
        player && player.pubkey && player.pubkey.length > 0
          ? `pk:${player.pubkey}`
          : `sid:${identity}`;
      el.muted = muted.has(key);
    });
  }

  const value = useMemo<LiveKitContextValue>(
    () => ({
      room: roomRef.current,
      getAudioElement: (identity) => audioElsRef.current.get(identity),
      getRemoteTrack: (identity) => tracksRef.current.get(identity),
    }),
    // roomRef is a ref; recreate the value object only when conn state
    // changes (so consumers re-read room). useVoiceStore would do that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [useVoiceStore((s) => s.conn)],
  );

  return <Ctx.Provider value={value}>{props.children}</Ctx.Provider>;
}
